import React from 'react';
import BoltRoundedIcon from '@mui/icons-material/BoltRounded';
import GridOnRoundedIcon from '@mui/icons-material/GridOnRounded';
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { getModifierMeta } from './boardConfig';
import { getRarity } from './config/rarityConfig';
import { SHOP_KINDS, SHOP_KIND_CHIP_LABELS } from './config/shopKinds';

function ModifierCard({ option, actionLabel, onAction, count }) {
  const theme = useTheme();
  const rarity = getRarity(option.rarity);
  const isBoardModifier = option.kind === SHOP_KINDS.BOARD_MODIFIER_GRANT;
  const kindLabel = SHOP_KIND_CHIP_LABELS[option.kind] || 'Modifier';
  const optionName = option.modifierLabel || kindLabel;
  const optionDescription = option.modifierDescription || option.description || '';

  const squareMeta = isBoardModifier && option.boardSquareModifierId
    ? getModifierMeta(option.boardSquareModifierId)
    : null;

  return (
    <Card
      variant="outlined"
      sx={{
        borderColor: alpha(rarity.color, 0.44),
        boxShadow: `0 0 0 1px ${alpha(rarity.color, 0.14)}, 0 4px 14px ${alpha(rarity.color, 0.12)}`,
        borderRadius: 2,
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        position: 'relative',
      }}
    >
      {count > 1 && (
        <Box
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            zIndex: 1,
            minWidth: 22,
            height: 22,
            borderRadius: '11px',
            backgroundColor: rarity.color,
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 11,
            fontWeight: 900,
            px: 0.6,
            boxShadow: `0 1px 4px ${alpha(rarity.color, 0.55)}`,
            userSelect: 'none',
          }}
        >
          ×{count}
        </Box>
      )}
      <CardContent sx={{ flex: 1, pb: onAction ? 0 : 1.5 }}>
        <Stack spacing={1}>
          <Stack direction="row" spacing={0.6} alignItems="center" flexWrap="wrap" useFlexGap>
            <Chip
              size="small"
              label={kindLabel}
              sx={{ height: 22, borderRadius: 1, fontWeight: 800, fontSize: 10.5 }}
            />
            <Chip
              size="small"
              label={rarity.label}
              sx={{
                height: 20,
                borderRadius: 1,
                fontWeight: 800,
                fontSize: 10,
                color: rarity.color,
                border: `1px solid ${alpha(rarity.color, 0.5)}`,
                backgroundColor: alpha(rarity.color, 0.1),
                boxShadow: `0 0 0 1px ${rarity.glow}`,
              }}
            />
          </Stack>

          {isBoardModifier && squareMeta ? (
            <Box
              sx={{
                height: 80,
                borderRadius: 1.5,
                backgroundColor: theme.palette.scrabble?.[squareMeta.colorKey] || theme.palette.action.selected,
                border: `1px solid ${alpha(theme.palette.text.primary, 0.16)}`,
                display: 'grid',
                placeItems: 'center',
              }}
            >
              <Typography sx={{ fontWeight: 900, fontSize: 20, letterSpacing: 0.5, userSelect: 'none' }}>
                {squareMeta.label}
              </Typography>
            </Box>
          ) : (
            <Box
              sx={{
                height: 80,
                borderRadius: 1.5,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.22)}`,
                background: `linear-gradient(140deg, ${alpha(theme.palette.primary.main, 0.12)} 0%, ${alpha(theme.palette.background.paper, 0.96)} 88%)`,
                display: 'grid',
                placeItems: 'center',
              }}
            >
              <Stack spacing={0.5} alignItems="center">
                <BoltRoundedIcon color="primary" sx={{ fontSize: 28 }} />
                <Typography sx={{ fontSize: 10, fontWeight: 800, opacity: 0.8, letterSpacing: 0.5 }}>
                  PERSISTENT
                </Typography>
              </Stack>
            </Box>
          )}

          <Stack spacing={0.3}>
            <Typography sx={{ fontWeight: 800, fontSize: 13.5, lineHeight: 1.25 }}>
              {optionName}
            </Typography>
            {optionDescription ? (
              <Typography sx={{ fontSize: 11.5, color: 'text.secondary', lineHeight: 1.4 }}>
                {optionDescription}
              </Typography>
            ) : null}
          </Stack>
        </Stack>
      </CardContent>

      {onAction ? (
        <CardActions sx={{ pt: 0.5, pb: 1.2, px: 1.5 }}>
          <Button
            fullWidth
            variant="contained"
            color="success"
            size="small"
            onClick={onAction}
            startIcon={<GridOnRoundedIcon />}
          >
            {actionLabel || 'Place'}
          </Button>
        </CardActions>
      ) : null}
    </Card>
  );
}

export default ModifierCard;
